import {
  isGroupedExportVariant,
  type DownloadSettingsModalVariant,
} from '@/app/(main)/client-detail/types/downloadSettingsModalVariant';
import type {
  UserExportAddress,
  UserExportBasicInformation,
  UserExportImportantDates,
  UserExportImportantTravelData,
  UserExportSettingsInner,
} from '@/types/userExportSettingsApiTypes';
import type { FormValues } from '../types';
import { comfortGranularAny } from './granularChecks';
import { comfortOnlyTravelPrefsCategory, effectiveTravelPrefsCategoryToggle } from './travelPreferences';

type BasicInformationOptions = {
  includeHomeAirports: boolean;
};

type AddressOptions = {
  includeBilling: boolean;
};

export function mapBasicInformationFromFormValues(
  values: FormValues,
  { includeHomeAirports }: BasicInformationOptions
): UserExportBasicInformation {
  return {
    toggle: values.include_basic_information,
    first_name: values.include_basic_information_first_name,
    middle_name: values.include_basic_information_middle_name,
    last_name: values.include_basic_information_last_name,
    primary_email: values.include_basic_information_primary_email,
    date_of_birth: values.include_basic_information_date_of_birth,
    phone: values.include_basic_information_phone,
    gender: values.include_basic_information_gender,
    home_airports: includeHomeAirports ? values.include_basic_information_home_airports : false,
    age: values.include_basic_information_age,
    relationship_to_primary: values.include_basic_information_relationship_to_primary,
  };
}

export function mapAddressFromFormValues(values: FormValues, { includeBilling }: AddressOptions): UserExportAddress {
  return {
    toggle: values.include_address,
    mailing: values.include_address_mailing,
    billing: includeBilling ? values.include_address_billing : false,
  };
}

export function mapImportantDatesFromFormValues(values: FormValues): UserExportImportantDates {
  return {
    toggle: values.include_important_dates,
    date_of_birth: values.include_important_dates_date_of_birth,
    confirmation_ceremony: values.include_important_dates_confirmation_ceremony,
  };
}

export function mapImportantTravelDataFromFormValues(values: FormValues): UserExportImportantTravelData {
  return {
    toggle: values.include_important_travel_data,
    global_entry_number: values.include_important_travel_data_global_entry_number,
    known_traveler_number: values.include_important_travel_data_known_traveler_number,
  };
}

export function mapTravelPreferencesByCategoryFromFormValues(
  values: FormValues,
  variant: DownloadSettingsModalVariant
): UserExportSettingsInner['include_travel_preferences_by_category'] {
  if (isGroupedExportVariant(variant)) {
    return comfortOnlyTravelPrefsCategory(values);
  }

  return {
    toggle: effectiveTravelPrefsCategoryToggle(values),
    travel: values.include_travel_preferences_by_category_travel,
    lifestyle: values.include_travel_preferences_by_category_lifestyle,
    stay: values.include_travel_preferences_by_category_stay,
    personal: values.include_travel_preferences_by_category_personal || comfortGranularAny(values),
  };
}

export function mapGroupViewExcludedFlags(values: FormValues, isGroupView: boolean) {
  return {
    include_emergency_contact: isGroupView ? false : values.include_emergency_contact,
    include_related_contacts: isGroupView ? false : values.include_related_contacts,
    include_notes: isGroupView ? false : values.include_notes,
    include_tasks: isGroupView ? false : values.include_tasks,
    include_client_documents: isGroupView ? false : values.include_client_documents,
  };
}
